// client/src/pages/Companies.jsx
import React, { useState, useEffect } from 'react';
import { ExternalLink, Calendar, Building, Lightbulb } from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';

const Companies = () => {
    const [companiesData, setCompaniesData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCompaniesData = async () => {
            try {
                const response = await fetch('/api/content/companies');
                const data = await response.json();
                setCompaniesData(data);
            } catch (error) {
                console.error('Error loading companies data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCompaniesData();
    }, []);

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
                </div>
            </SEOLayout>
        );
    }

    return (
        <SEOLayout
            title="Companies"
            description={companiesData?.hero?.description}
            keywords="Louis Silverstein companies, Baraqu LLC, XRMGR, PostMGR, technology ventures"
        >
            {/* Hero Section */}
            <section className="bg-orange-700 py-16" aria-labelledby="companies-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 id="companies-heading" className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl">
                            {companiesData?.hero?.title}
                        </h1>
                        <p className="mt-6 max-w-2xl mx-auto text-xl text-orange-100">
                            {companiesData?.hero?.description}
                        </p>
                    </div>
                </div>
            </section>

            {/* Current Companies */}
            <section className="py-16 bg-white" aria-labelledby="current-companies-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center mb-12">
                        <h2 id="current-companies-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                            Active Ventures
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            Current Companies
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-1">
                        {companiesData?.current_companies?.map((company) => (
                            <article key={company.id} className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                <div className="p-8">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <Building className="h-8 w-8 text-orange-600" aria-hidden="true" />
                                                <div>
                                                    <h3 className="text-2xl font-bold text-gray-900">{company.name}</h3>
                                                    <p className="text-orange-600 font-medium">{company.tagline}</p>
                                                </div>
                                            </div>

                                            <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                                                {company.description}
                                            </p>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                                <div>
                                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Focus Areas</h4>
                                                    <ul className="space-y-2">
                                                        {company.focus_areas?.map((area, index) => (
                                                            <li key={index} className="flex items-center text-gray-600">
                                                                <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                                                                {area}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                {company.notable_projects && (
                                                    <div>
                                                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Notable Projects</h4>
                                                        <ul className="space-y-2">
                                                            {company.notable_projects.slice(0, 3).map((project, index) => (
                                                                <li key={index} className="flex items-start text-gray-600">
                                                                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                                                    {project}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {company.features && (
                                                    <div>
                                                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h4>
                                                        <ul className="space-y-2">
                                                            {company.features.slice(0, 3).map((feature, index) => (
                                                                <li key={index} className="flex items-start text-gray-600">
                                                                    <div className="w-2 h-2 bg-purple-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                                                    {feature}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center text-sm text-gray-500">
                                                    <Calendar className="h-4 w-4 mr-1" aria-hidden="true" />
                                                    Founded {company.founded}
                                                </div>
                                                {company.website && (
                                                    <a
                                                        href={company.website}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors duration-200"
                                                        aria-label={`Visit ${company.name} website`}
                                                    >
                                                        Visit Website
                                                        <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Past Ventures */}
            {companiesData?.past_ventures && companiesData.past_ventures.length > 0 && (
                <section className="py-16 bg-gray-50" aria-labelledby="past-ventures-heading">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="lg:text-center mb-12">
                            <h2 id="past-ventures-heading" className="text-base text-orange-600 font-semibold tracking-wide uppercase">
                                Previous Ventures
                            </h2>
                            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                                Past Companies
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {companiesData.past_ventures.map((company) => (
                                <article key={company.id} className="bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex items-center space-x-3 mb-4">
                                        <Building className="h-6 w-6 text-gray-500" aria-hidden="true" />
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900">{company.name}</h3>
                                            <p className="text-gray-600 text-sm">{company.tagline}</p>
                                        </div>
                                    </div>

                                    <p className="text-gray-700 mb-4">
                                        {company.description}
                                    </p>

                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Focus Areas</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {company.focus_areas?.slice(0, 3).map((area, index) => (
                                                <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    {area}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-sm text-gray-500">
                                        <span>{company.founded} - {company.concluded}</span>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            {company.status}
                                        </span>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Philosophy Section */}
            {companiesData?.philosophy && (
                <section className="py-16 bg-orange-50" aria-labelledby="philosophy-heading">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 id="philosophy-heading" className="text-3xl font-extrabold text-gray-900 mb-4">
                                {companiesData.philosophy.title}
                            </h2>
                            <p className="text-xl text-gray-700 leading-relaxed">
                                {companiesData.philosophy.description}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {companiesData.philosophy.principles?.map((principle, index) => (
                                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-start">
                                        <Lightbulb className="h-6 w-6 text-orange-600 mr-3 mt-1 flex-shrink-0" aria-hidden="true" />
                                        <p className="text-gray-700">{principle}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Call to Action */}
            <section className="bg-orange-700" aria-labelledby="companies-cta-heading">
                <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
                    <h2 id="companies-cta-heading" className="text-3xl font-extrabold text-white sm:text-4xl">
                        <span className="block">Interested in collaboration?</span>
                        <span className="block">Let's explore opportunities together.</span>
                    </h2>
                    <p className="mt-4 text-lg leading-6 text-orange-200">
                        From technical consulting to joint ventures, I'm always open to discussing innovative projects.
                    </p>
                    <a
                        href="/contact"
                        className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-orange-700 sm:w-auto transition-colors duration-200"
                    >
                        Get In Touch
                    </a>
                </div>
            </section>
        </SEOLayout>
    );
};

export default Companies;